import { Alert, AlertTitle, AlertDescription } from "./components/ui/alert";
import { Button } from "./components/ui/button";
import { WarningIcon, ArrowClockwiseIcon } from "@phosphor-icons/react";

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

export const ErrorFallback = ({ error, resetError }: ErrorFallbackProps) => {
  // When encountering an error in the development mode, rethrow it and don't display the boundary.
  // The parent UI will take care of showing a more helpful dialog.
  if (import.meta.env.DEV) throw error;

  // Chunk load failures happen when a new deploy invalidates old hashed filenames.
  // Force a full reload once so the browser fetches the new index.html and chunks.
  const isChunkError =
    error?.message?.includes("Failed to fetch dynamically imported module") ||
    // iOS/WebKit reports dynamic import failures as "Load failed" (no module mention)
    error?.message?.includes("Load failed") ||
    error?.message?.includes("Loading chunk") ||
    error?.message?.includes("is not a valid JavaScript MIME type") ||
    error?.name === "ChunkLoadError";

  if (isChunkError) {
    const reloadKey = "__chunk_reload";
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, "1");
      window.location.reload();
      return null;
    }
    // Already tried once — fall through to error UI
    sessionStorage.removeItem(reloadKey);
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Alert variant="destructive" className="mb-6">
          <WarningIcon />
          <AlertTitle>La aplicación ha encontrado un error</AlertTitle>
          <AlertDescription>
            Algo inesperado ocurrió mientras se ejecutaba la aplicación. Los
            detalles del error se muestran a continuación.
          </AlertDescription>
        </Alert>

        <div className="bg-card border rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-sm text-muted-foreground mb-2">
            Error Details:
          </h3>
          <pre className="text-xs text-destructive bg-muted/50 p-3 rounded border overflow-auto max-h-32">
            {error.message}
          </pre>
        </div>

        <Button onClick={resetError} className="w-full" variant="outline">
          <ArrowClockwiseIcon />
          Try Again
        </Button>
      </div>
    </div>
  );
};
